import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import Dashboard from './pages/Dashboard';
import FocusedDashboard from './pages/FocusedDashboard';
import Settings from './pages/Settings';
import AdminIdeas from './pages/AdminIdeas';
import AdminTerminal from './pages/AdminTerminal';
import AdminAuditLog from './pages/AdminAuditLog';
import TerminalWindow from './pages/TerminalWindow';
import ServiceHealthTicker from './components/ServiceHealthTicker';
import FeedbackWidget from './components/FeedbackWidget';
import FeedbackReviewOverlay from './components/FeedbackReviewOverlay';
import { getThemePreference, setThemePreference, migrateLocalStorageToServer } from './services/userPreferences';

import './App.css';

// Admin-only access
const ADMIN_EMAIL = 'jgoode@umetech.net';

// Tools available in the Tools flyout menu
const TOOLS = [
  {
    id: 'zoneshift',
    name: 'ZoneShift',
    icon: '\u{1F504}',
    description: 'DNS Migration Verification',
    url: 'https://zoneshift.umetech.net',
  },
  {
    id: 'sightline',
    name: 'Sightline',
    icon: '\u{1F4CA}',
    description: 'Visitor Analytics',
    url: 'https://sightline.umetech.net',
  },
];

function AppContent() {
  const { isAuthenticated, isLoading, user, login, logout } = useAuth();
  const location = useLocation();

  // Theme state - initialize from localStorage for fast render, then sync with server
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('umt-hud-theme');
    return (saved === 'light' ? 'light' : 'dark') as 'dark' | 'light';
  });
  const [themeSynced, setThemeSynced] = useState(false);

  // User dropdown state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Feedback review overlay state
  const [isReviewActive, setIsReviewActive] = useState(false);

  // Service Health modal trigger from dropdown
  const [showServiceHealth, setShowServiceHealth] = useState(false);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('umt-hud-theme', theme);
  }, [theme]);

  // Sync theme from server after authentication
  useEffect(() => {
    if (isAuthenticated && !themeSynced) {
      // Migrate localStorage to server (one-time, if needed)
      migrateLocalStorageToServer().catch(console.warn);

      // Fetch theme from server
      getThemePreference()
        .then((serverTheme) => {
          if (serverTheme !== theme) {
            setTheme(serverTheme);
          }
          setThemeSynced(true);
        })
        .catch((err) => {
          console.warn('Failed to fetch theme from server:', err);
          setThemeSynced(true);
        });
    }
  }, [isAuthenticated, themeSynced, theme]);

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setShowUserMenu(false);
  }, [location]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    // Save to server (async, fire-and-forget with localStorage as immediate backup)
    setThemePreference(newTheme).catch(console.warn);
  };

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-card">
          <img src="/umetech-logo.png" alt="Umetech" className="login-logo" />
          <h1>Mission Control HUD</h1>
          <p>Unified Dashboard for UMT Tools</p>
          <button className="login-button" onClick={login}>
            Sign in with Microsoft
          </button>
          <p className="login-note">Use your @umetech.net account</p>
        </div>
      </div>
    );
  }

  // Show app when authenticated
  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <Link to="/" className="header-brand">
            <div className="logo-container">
              <img src="/umetech-logo.png" alt="Umetech" className="header-logo" />
              <h1 className="header-title">Mission Control HUD</h1>
            </div>
          </Link>
        </div>
        {/* Mini-widgets portal - rendered by FocusedDashboard */}
        <div id="mini-widgets-portal" className="header-center"></div>
        <div className="header-right">
          {/* User area with controls below */}
          <div className="user-area">
            <div className="user-menu-container" ref={userMenuRef}>
              <button
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <span className="user-name">{user?.name || 'User'}</span>
                <span className="dropdown-arrow">{showUserMenu ? '▲' : '▼'}</span>
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <span className="dropdown-name">{user?.name}</span>
                    <span className="dropdown-email">{user?.username}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-controls">
                    <div className="dropdown-control-row">
                      <span className="dropdown-control-label">Theme</span>
                      <label className="theme-slider" title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
                        <span className="theme-icon">🌙</span>
                        <input
                          type="checkbox"
                          checked={theme === 'light'}
                          onChange={toggleTheme}
                        />
                        <span className="theme-slider-track"></span>
                        <span className="theme-icon">☀️</span>
                      </label>
                    </div>
                    <div id="refresh-controls-portal" className="dropdown-control-row"></div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <Link to="/settings" className="dropdown-item">
                    <span className="dropdown-icon">⚙️</span>
                    Settings
                  </Link>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowServiceHealth(true);
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="dropdown-icon">📡</span>
                    Service Health
                  </button>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-tools-trigger">
                    <span className="dropdown-icon">{'\u{1F6E0}\u{FE0F}'}</span>
                    Tools
                    <span className="tools-flyout-arrow">{'\u{203A}'}</span>
                    <div className="tools-flyout">
                      {TOOLS.map((tool) => (
                        <button
                          key={tool.id}
                          className="tools-flyout-item"
                          onClick={() => {
                            window.open(tool.url, tool.id, 'width=1280,height=900,menubar=no,toolbar=no');
                            setShowUserMenu(false);
                          }}
                        >
                          <span className="tools-flyout-icon">{tool.icon}</span>
                          <div className="tools-flyout-info">
                            <span className="tools-flyout-name">{tool.name}</span>
                            <span className="tools-flyout-desc">{tool.description}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {user?.username?.toLowerCase() === ADMIN_EMAIL && (
                    <>
                      <div className="dropdown-divider"></div>
                      <div className="dropdown-section-label">Admin Only</div>
                      <Link to="/admin/ideas" className="dropdown-item">
                        <span className="dropdown-icon">💡</span>
                        Ideas & Notes
                      </Link>
                      <Link to="/admin/terminal" className="dropdown-item">
                        <span className="dropdown-icon">💻</span>
                        Terminal
                      </Link>
                      <Link to="/admin/audit-log" className="dropdown-item">
                        <span className="dropdown-icon">📋</span>
                        Audit Log
                      </Link>
                    </>
                  )}
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout" onClick={logout}>
                    <span className="dropdown-icon">🚪</span>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            <span className="version-below-dropdown">
              v1.4.81 · <a
                href="https://github.com/UMTJG00d3/UMT-HUD/blob/master/docs/CHANGELOG.md"
                target="_blank"
                rel="noopener noreferrer"
                className="changelog-link-below"
              >Changelog</a>
            </span>
          </div>
        </div>
      </header>
      <ServiceHealthTicker
        showAllModal={showServiceHealth}
        onAllModalClose={() => setShowServiceHealth(false)}
      />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard/:widgetId/*" element={<FocusedDashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/terminal" element={<TerminalWindow />} />
          {user?.username?.toLowerCase() === ADMIN_EMAIL && (
            <>
              <Route path="/admin/ideas" element={<AdminIdeas />} />
              <Route path="/admin/terminal" element={<AdminTerminal />} />
              <Route path="/admin/audit-log" element={<AdminAuditLog />} />
            </>
          )}
        </Routes>
      </main>
      <FeedbackWidget hidden={isReviewActive} />
      <FeedbackReviewOverlay onActiveChange={setIsReviewActive} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
